import React, { useState, useEffect, useMemo } from 'react';
import {
  Clock,
  CheckCircle,
  XCircle,
  UserCheck,
  UserX,
  LogIn,
  LogOut,
  Calendar,
  Monitor,
  Zap,
  Info,
  DoorOpen,
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import toast from 'react-hot-toast';
import { ReportButton } from '../../ui/ReportButton';
import { api } from '../../../services/api';
import { cn } from '../../../utils/cn';
import { accessHistoryBadge } from '../../../utils/eventHelpers';
import { usePersonDetailContext } from '../../../contexts/PersonDetailContext';
import { usePorteiroReports } from '../../../hooks/usePorteiroReports';

interface AccessHistoryTabProps {
  personId: string;
  personName?: string;
  personPhotoUrl?: string;
  darkMode?: boolean;
}

interface AccessEvent {
  id: string;
  event_type: string;
  event_timestamp: string;
  is_authorized: boolean;
  device_name?: string;
  zone_name?: string;
  event_category?: string;
  severity?: string;
  message?: string;
  description?: string;
  status?: string;
  person_type?: string;
  direction?: string; // 'entrada' | 'saida' | 'passagem'
  details?: {
    authorized_by_name?: string;
    authorized_by_resident_id?: string;
    unit?: string;
    observations?: string;
    manual_operation?: boolean;
    [key: string]: any;
  };
}

// ── Date helpers (internal state is always DD/MM/YYYY) ────────────────────────
const isoToBR = (iso: string): string => {
  if (!iso || iso.length !== 10) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};
const brToISO = (br: string): string => {
  if (!br || br.length !== 10) return '';
  const [d, m, y] = br.split('/');
  return `${y}-${m}-${d}`;
};
const maskDateInput = (raw: string): string => {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};
// Use local date components (NOT toISOString which returns UTC, causing off-by-one
// in timezones behind UTC like São Paulo UTC-3).
const localDateBR = (d: Date): string => {
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
};
const todayBR = () => localDateBR(new Date());
const thirtyDaysAgoBR = () => {
  const d = new Date(); d.setDate(d.getDate() - 30); return localDateBR(d);
};
const nDaysAgoBR = (n: number): string => {
  const d = new Date(); d.setDate(d.getDate() - n); return localDateBR(d);
};

// ── Quick filter presets ──────────────────────────────────────────────────────
const QUICK_RANGES = [
  { label: 'Tudo',    start: '',              end: ''           },
  { label: 'Hoje',   start: todayBR(),        end: todayBR()   },
  { label: '7 dias', start: nDaysAgoBR(7),    end: todayBR()   },
  { label: '30 dias',start: nDaysAgoBR(30),   end: todayBR()   },
  { label: '90 dias',start: nDaysAgoBR(90),   end: todayBR()   },
] as const;

export const AccessHistoryTab: React.FC<AccessHistoryTabProps> = ({ personId, personName, personPhotoUrl, darkMode = false }) => {
  const canViewReports = usePorteiroReports();
  const { openPersonDetail } = usePersonDetailContext();

  // ── Raw events from API ───────────────────────────────────────────────────
  const [allLoadedEvents, setAllLoadedEvents] = useState<AccessEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  // ── Inline filter state ───────────────────────────────────────────────────
  const [filterStart, setFilterStart] = useState('');   // applied to API ('' = no filter)
  const [filterEnd, setFilterEnd] = useState('');
  const [draftStart, setDraftStart] = useState('');     // live input value
  const [draftEnd, setDraftEnd] = useState('');
  const [activeQuick, setActiveQuick] = useState('Tudo');
  const [deviceFilter, setDeviceFilter] = useState(''); // client-side device filter
  const [filterError, setFilterError] = useState('');
  // Bumped to force a re-fetch even when filter values didn't change (e.g. same quick button)
  const [fetchKey, setFetchKey] = useState(0);

  // ── Report state ──────────────────────────────────────────────────────────
  const [generatingReport, setGeneratingReport] = useState(false);

  // ── Dates used for report generation (current filter or 30-day default) ──
  const reportStart = filterStart.length === 10 ? filterStart : thirtyDaysAgoBR();
  const reportEnd   = filterEnd.length   === 10 ? filterEnd   : todayBR();

  // ── Unique devices from loaded events (for dropdown) ─────────────────────
  const knownDevices = useMemo(() => {
    const names = allLoadedEvents.map(e => e.device_name).filter(Boolean) as string[];
    return Array.from(new Set(names)).sort();
  }, [allLoadedEvents]);

  // ── Client-side filter: local-timezone date + device ─────────────────────
  // The API may filter by UTC dates; we re-filter client-side by local date
  // to handle the timezone offset (Brazil UTC-3 causes late-night events to
  // appear on the "next day" in UTC while the local display shows the correct date).
  const events = useMemo(() => {
    const startISO = filterStart.length === 10 ? brToISO(filterStart) : '';
    const endISO   = filterEnd.length   === 10 ? brToISO(filterEnd)   : '';

    return allLoadedEvents.filter(ev => {
      // Local date of this event (device local time, not UTC)
      if (startISO || endISO) {
        const d = new Date(ev.event_timestamp);
        const localISO = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        if (startISO && localISO < startISO) return false;
        if (endISO   && localISO > endISO)   return false;
      }
      if (deviceFilter && ev.device_name !== deviceFilter) return false;
      return true;
    });
  }, [allLoadedEvents, filterStart, filterEnd, deviceFilter]);

  // ── Fetch effect ──────────────────────────────────────────────────────────
  // Runs on: mount, page change (load more), filter change (fetchKey bump).
  // Does NOT call setAllLoadedEvents([]) before fetching — keeps old events
  // visible while loading to avoid the "empty flash → spinner" visual glitch.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params: Record<string, any> = { page, limit: 20 };
        if (filterStart.length === 10) params.start_date = brToISO(filterStart);
        if (filterEnd.length === 10)   params.end_date   = brToISO(filterEnd);
        const response = await api.get(`/people/${personId}/access-history`, { params });
        if (cancelled) return;
        const newEvents: AccessEvent[] = response.data.data || [];
        // page === 1 → replace list; page > 1 → append (load more)
        setAllLoadedEvents(prev => page === 1 ? newEvents : [...prev, ...newEvents]);
        setHasMore(newEvents.length === 20);
      } catch (err) {
        if (!cancelled) console.error('Failed to fetch access history:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [personId, page, filterStart, filterEnd, fetchKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Trigger a page-1 re-fetch (shared by quick and manual apply) ──────────
  // Always bumps fetchKey so the effect fires even when filter values are identical.
  const triggerFetch = (start: string, end: string) => {
    setFilterStart(start);
    setFilterEnd(end);
    setPage(1);
    setFetchKey(k => k + 1);
  };

  // ── Apply quick range preset ──────────────────────────────────────────────
  const applyQuick = (label: string, start: string, end: string) => {
    setActiveQuick(label);
    setDraftStart(start);
    setDraftEnd(end);
    setFilterError('');
    setDeviceFilter('');
    triggerFetch(start, end);
  };

  // ── Apply manual date input ───────────────────────────────────────────────
  const applyDraftFilter = () => {
    if (draftStart || draftEnd) {
      if (draftStart.length !== 10 || draftEnd.length !== 10) {
        setFilterError('Preencha as duas datas no formato DD/MM/AAAA.');
        return;
      }
      const isoS = brToISO(draftStart);
      const isoE = brToISO(draftEnd);
      if (!isoS || !isoE || isNaN(Date.parse(isoS)) || isNaN(Date.parse(isoE))) {
        setFilterError('Data inválida. Use DD/MM/AAAA.');
        return;
      }
      if (isoS > isoE) {
        setFilterError('Data inicial não pode ser posterior à final.');
        return;
      }
    }
    setFilterError('');
    setActiveQuick('custom');
    setDeviceFilter('');
    triggerFetch(draftStart, draftEnd);
  };

  const loadMore = () => {
    if (!loading && hasMore) setPage(p => p + 1);
  };

  // ── Filter events by local date (same logic used for display) ───────────
  // The API ignores start_date/end_date params, so we always filter client-side.
  const filterByLocalDate = (evs: AccessEvent[], startBR: string, endBR: string): AccessEvent[] => {
    const startISO = startBR.length === 10 ? brToISO(startBR) : '';
    const endISO   = endBR.length   === 10 ? brToISO(endBR)   : '';
    if (!startISO && !endISO) return evs;
    return evs.filter(ev => {
      const d = new Date(ev.event_timestamp);
      const localISO = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      if (startISO && localISO < startISO) return false;
      if (endISO   && localISO > endISO)   return false;
      return true;
    });
  };

  // ── Fetch all events for report then filter client-side ──────────────────
  const fetchEventsForReport = async (startBR: string, endBR: string): Promise<AccessEvent[]> => {
    const all: AccessEvent[] = [];
    let p = 1;
    // Still pass params — if the API ever supports them it will reduce data volume
    const apiParams: Record<string, any> = { limit: 100 };
    if (startBR.length === 10) apiParams.start_date = brToISO(startBR);
    if (endBR.length   === 10) apiParams.end_date   = brToISO(endBR);
    while (true) {
      const response = await api.get(`/people/${personId}/access-history`, {
        params: { ...apiParams, page: p },
      });
      const batch: AccessEvent[] = response.data.data || [];
      all.push(...batch);
      if (batch.length < 100) break;
      p++;
    }
    // Always re-filter by local date — API date params use UTC, we need local
    return filterByLocalDate(all, startBR, endBR);
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return {
      date: date.toLocaleDateString('pt-BR'),
      time: date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
    };
  };

  const formatDirection = (dir?: string): string => {
    switch (dir?.toLowerCase()) {
      case 'entrada':  return 'Entrada';
      case 'saida':    return 'Saída';
      case 'passagem': return 'Passagem';
      default: return dir || '—';
    }
  };

  interface EventDisplay {
    label: string;
    icon: React.ReactNode;
    bgColor: string;
    textColor: string;
    borderColor: string;
  }

  const getEventDisplay = (event: AccessEvent, dm: boolean): EventDisplay => {
    const iconClass = "w-6 h-6 flex-shrink-0 mt-0.5";
    switch (event.event_type) {
      case 'authorization':
        return { label: 'Autorização Concedida', icon: <UserCheck className={`${iconClass} text-green-500`} />, bgColor: dm ? 'bg-green-900/20' : 'bg-green-50', textColor: dm ? 'text-green-400' : 'text-green-700', borderColor: dm ? 'border-green-800' : 'border-green-200' };
      case 'revoke':
        return { label: 'Autorização Revogada', icon: <UserX className={`${iconClass} text-red-500`} />, bgColor: dm ? 'bg-red-900/20' : 'bg-red-50', textColor: dm ? 'text-red-400' : 'text-red-700', borderColor: dm ? 'border-red-800' : 'border-red-200' };
      case 'manual_check_in':
        return { label: 'Entrada Manual', icon: <LogIn className={`${iconClass} text-blue-500`} />, bgColor: dm ? 'bg-blue-900/20' : 'bg-blue-50', textColor: dm ? 'text-blue-400' : 'text-blue-700', borderColor: dm ? 'border-blue-800' : 'border-blue-200' };
      case 'manual_check_out':
        return { label: 'Saída Manual', icon: <LogOut className={`${iconClass} text-orange-500`} />, bgColor: dm ? 'bg-orange-900/20' : 'bg-orange-50', textColor: dm ? 'text-orange-400' : 'text-orange-700', borderColor: dm ? 'border-orange-800' : 'border-orange-200' };
      case 'auto_check_in':
        return { label: 'Entrada Automática', icon: <Zap className={`${iconClass} text-teal-500`} />, bgColor: dm ? 'bg-teal-900/20' : 'bg-teal-50', textColor: dm ? 'text-teal-400' : 'text-teal-700', borderColor: dm ? 'border-teal-800' : 'border-teal-200' };
      case 'auto_check_out':
        return { label: 'Saída Automática', icon: <Zap className={`${iconClass} text-blue-500`} />, bgColor: dm ? 'bg-blue-900/20' : 'bg-blue-50', textColor: dm ? 'text-blue-400' : 'text-blue-700', borderColor: dm ? 'border-blue-800' : 'border-blue-200' };
      case 'remote_door_trigger':
      case 'remote_user':
        // The report said "Acesso Autorizado" for these, which reads as a reader letting
        // somebody through. Nobody was at the reader: the gate was opened from the app.
        return { label: 'Abertura Remota', icon: <DoorOpen className={`${iconClass} text-violet-500`} />, bgColor: dm ? 'bg-violet-900/20' : 'bg-violet-50', textColor: dm ? 'text-violet-400' : 'text-violet-700', borderColor: dm ? 'border-violet-800' : 'border-violet-200' };
      default:
        return event.is_authorized
          ? { label: 'Acesso Autorizado', icon: <CheckCircle className={`${iconClass} text-green-500`} />, bgColor: dm ? 'bg-gray-800' : 'bg-gray-50', textColor: dm ? 'text-green-400' : 'text-green-700', borderColor: dm ? 'border-gray-700' : 'border-gray-200' }
          : { label: 'Acesso Negado',     icon: <XCircle className={`${iconClass} text-red-500`} />,   bgColor: dm ? 'bg-gray-800' : 'bg-gray-50', textColor: dm ? 'text-red-400'   : 'text-red-700',   borderColor: dm ? 'border-gray-700' : 'border-gray-200' };
    }
  };

  // ── CSV export — uses current filter dates (or 30-day default) ──────────
  const handleExportCSV = async () => {
    setGeneratingReport(true);
    try {
      const allEvents = await fetchEventsForReport(reportStart, reportEnd);
      const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
      const generatedAt = new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
      const headerBlock = [
        `${escape('Relatório de Histórico de Acesso')}`,
        `${escape('Pessoa:')},${escape(personName || personId)}`,
        `${escape('Período:')},${escape(`${reportStart} a ${reportEnd}`)}`,
        `${escape('Gerado em:')},${escape(generatedAt)}`,
        `${escape('Total de eventos:')},${escape(String(allEvents.length))}`,
        '',
      ];
      const headers = ['Data', 'Hora', 'Tipo de Evento', 'Autorizado', 'Dispositivo', 'Tipo de Acesso'];
      const rows = allEvents.map(ev => {
        const { date, time } = formatTimestamp(ev.event_timestamp);
        const display = getEventDisplay(ev, false);
        return [date, time, display.label, ev.is_authorized ? 'Sim' : 'Não', ev.device_name || '', formatDirection(ev.direction)]
          .map(v => escape(String(v))).join(',');
      });
      const csv = [...headerBlock, headers.join(','), ...rows].join('\r\n');
      const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = (personName || personId).replace(/[^a-zA-Z0-9_\-]/g, '_');
      a.download = `historico_acesso_${safeName}_${reportStart.replace(/\//g, '-')}_${reportEnd.replace(/\//g, '-')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('CSV export failed:', err);
      toast.error('Erro ao gerar CSV');
    } finally {
      setGeneratingReport(false);
    }
  };

  // ── PDF export — uses current filter dates (or 30-day default) ──────────
  // Uses jsPDF + autoTable (same pattern as every other PDF export in this app) and
  // triggers a real doc.save() file download. The previous implementation built an
  // HTML string and, outside Electron, printed it via a hidden iframe's
  // contentWindow.print() called from inside a setTimeout — by the time that fired,
  // the click's user-gesture context had expired, so browsers would silently block
  // it as an unrequested popup/print. No file was ever produced.
  const handleExportPDF = async () => {
    setGeneratingReport(true);
    try {
      const allEvents = await fetchEventsForReport(reportStart, reportEnd);
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageW = doc.internal.pageSize.getWidth();
      const margin = 14;
      let y = margin;

      try {
        const logoImg = new Image();
        logoImg.src = '/panther-logo.png';
        await new Promise<void>((resolve) => {
          logoImg.onload = () => { try { doc.addImage(logoImg, 'PNG', margin, 6, 20, 20); } catch {} resolve(); };
          logoImg.onerror = () => resolve();
          setTimeout(() => resolve(), 2000);
        });
      } catch {}

      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 30, 30);
      doc.text('ACCESS', margin + 23, 20);

      doc.setLineWidth(0.5);
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, 30, pageW - margin, 30);

      y = 38;
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 30, 30);
      doc.text('Histórico de Acesso', margin, y);

      y += 6;
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      const generatedAt = new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });
      doc.text(`Pessoa: ${personName || personId}`, margin, y);
      doc.text(`Período: ${reportStart} — ${reportEnd}`, margin + 100, y);
      doc.text(`Gerado em: ${generatedAt}`, pageW - margin, y, { align: 'right' });
      y += 8;

      autoTable(doc, {
        startY: y,
        head: [['Data', 'Hora', 'Tipo de Evento', 'Status', 'Dispositivo', 'Tipo de Acesso']],
        body: allEvents.map((ev) => {
          const { date, time } = formatTimestamp(ev.event_timestamp);
          const display = getEventDisplay(ev, false);
          return [
            date,
            time,
            display.label,
            ev.is_authorized ? 'Autorizado' : 'Negado',
            ev.device_name || '—',
            formatDirection(ev.direction),
          ];
        }),
        margin: { left: margin, right: margin },
        headStyles: { fillColor: [17, 24, 39], textColor: 255, fontSize: 8, fontStyle: 'bold' },
        bodyStyles: { fontSize: 8, textColor: [40, 40, 40] },
        alternateRowStyles: { fillColor: [248, 248, 248] },
        didParseCell: (data: any) => {
          if (data.section === 'body' && data.column.index === 3) {
            data.cell.styles.textColor = data.cell.raw === 'Autorizado' ? [22, 101, 52] : [185, 28, 28];
            data.cell.styles.fontStyle = 'bold';
          }
        },
      });

      const totalPages = (doc as any).internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(7);
        doc.setTextColor(160, 160, 160);
        doc.text(`Página ${i} de ${totalPages}`, pageW - margin, doc.internal.pageSize.getHeight() - 6, { align: 'right' });
        doc.text('Panther Access', margin, doc.internal.pageSize.getHeight() - 6);
      }

      const safeName = (personName || personId).replace(/[^a-zA-Z0-9_\-]/g, '_');
      doc.save(`historico_acesso_${safeName}_${reportStart.replace(/\//g, '-')}_${reportEnd.replace(/\//g, '-')}.pdf`);
    } catch (err) {
      console.error('PDF export failed:', err);
      toast.error('Erro ao gerar PDF');
    } finally {
      setGeneratingReport(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (!canViewReports) {
    return (
      <div className={cn("flex flex-col items-center justify-center py-12 gap-2", darkMode ? "text-gray-500" : "text-gray-400")}>
        <span className="text-sm">Relatórios desabilitados para porteiro</span>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* ── Inline filter bar ─────────────────────────────────────────────── */}
      <div className={cn(
        "rounded-lg border p-3 space-y-2",
        darkMode ? "bg-gray-800/60 border-gray-700" : "bg-gray-50 border-gray-200"
      )}>
        {/* Quick presets + export buttons on the same row */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {QUICK_RANGES.map(({ label, start, end }) => (
            <button
              key={label}
              onClick={() => applyQuick(label, start, end)}
              className={cn(
                "px-2.5 py-1 rounded text-xs font-medium transition-colors",
                activeQuick === label
                  ? "bg-blue-600 text-white"
                  : darkMode
                    ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                    : "bg-white text-gray-600 border border-gray-300 hover:bg-gray-100"
              )}
            >
              {label}
            </button>
          ))}
          {/* Spacer */}
          <span className="flex-1" />
          {/* Export buttons */}
          <ReportButton
            dark={darkMode}
            loading={generatingReport}
            formats={[
              { key: 'pdf', label: 'Exportar PDF', onSelect: handleExportPDF },
              { key: 'csv', label: 'Exportar CSV', onSelect: handleExportCSV },
            ]}
          />
        </div>

        {/* Date inputs + device dropdown */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 flex-shrink-0">
            <Calendar className={cn("w-3.5 h-3.5", darkMode ? "text-gray-400" : "text-gray-400")} />
            <span className={cn("text-xs", darkMode ? "text-gray-400" : "text-gray-500")}>De</span>
            <input
              type="text"
              inputMode="numeric"
              placeholder="DD/MM/AAAA"
              maxLength={10}
              value={draftStart}
              onChange={e => { setDraftStart(maskDateInput(e.target.value)); setFilterError(''); setActiveQuick('custom'); }}
              onKeyDown={e => e.key === 'Enter' && applyDraftFilter()}
              className={cn(
                "w-[7.5rem] px-2 py-1 rounded border text-xs font-mono",
                darkMode
                  ? "bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-500 focus:border-blue-500"
                  : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500",
                "outline-none focus:ring-1 focus:ring-blue-500/30"
              )}
            />
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <span className={cn("text-xs", darkMode ? "text-gray-400" : "text-gray-500")}>Até</span>
            <input
              type="text"
              inputMode="numeric"
              placeholder="DD/MM/AAAA"
              maxLength={10}
              value={draftEnd}
              onChange={e => { setDraftEnd(maskDateInput(e.target.value)); setFilterError(''); setActiveQuick('custom'); }}
              onKeyDown={e => e.key === 'Enter' && applyDraftFilter()}
              className={cn(
                "w-[7.5rem] px-2 py-1 rounded border text-xs font-mono",
                darkMode
                  ? "bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-500 focus:border-blue-500"
                  : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500",
                "outline-none focus:ring-1 focus:ring-blue-500/30"
              )}
            />
          </div>
          <button
            onClick={applyDraftFilter}
            className="flex-shrink-0 px-2.5 py-1 rounded bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 transition-colors"
          >
            Aplicar
          </button>

          {/* Device filter — only shown once at least one device is known */}
          {knownDevices.length > 0 && (
            <div className="flex items-center gap-1 flex-1 min-w-0">
              <Monitor className={cn("w-3.5 h-3.5 flex-shrink-0", darkMode ? "text-gray-400" : "text-gray-400")} />
              <select
                value={deviceFilter}
                onChange={e => setDeviceFilter(e.target.value)}
                className={cn(
                  "flex-1 min-w-0 px-2 py-1 rounded border text-xs truncate",
                  darkMode
                    ? "bg-gray-700 border-gray-600 text-gray-100 focus:border-blue-500"
                    : "bg-white border-gray-300 text-gray-900 focus:border-blue-500",
                  "outline-none focus:ring-1 focus:ring-blue-500/30"
                )}
              >
                <option value="">Todos os dispositivos</option>
                {knownDevices.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
          )}
        </div>

        {filterError && <p className="text-xs text-red-500">{filterError}</p>}

        {/* Active filter summary */}
        {(filterStart || filterEnd || deviceFilter) && (
          <div className={cn("flex items-center gap-2 text-xs", darkMode ? "text-gray-400" : "text-gray-500")}>
            <span>Filtrando:</span>
            {(filterStart || filterEnd) && (
              <span className={cn("px-1.5 py-0.5 rounded font-medium", darkMode ? "bg-blue-900/40 text-blue-300" : "bg-blue-50 text-blue-700")}>
                {filterStart || '…'} → {filterEnd || '…'}
              </span>
            )}
            {deviceFilter && (
              <span className={cn("px-1.5 py-0.5 rounded font-medium", darkMode ? "bg-blue-900/40 text-blue-300" : "bg-blue-50 text-blue-700")}>
                {deviceFilter}
              </span>
            )}
            <button
              onClick={() => applyQuick('Tudo', '', '')}
              className={cn("ml-auto text-xs underline hover:no-underline", darkMode ? "text-gray-400" : "text-gray-400")}
            >
              Limpar
            </button>
          </div>
        )}
      </div>

      {/* ── Events list ───────────────────────────────────────────────────── */}
      {/* Full-page spinner only on initial load (no events yet) */}
      {loading && events.length === 0 && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      )}
      {/* Empty state — only when not loading */}
      {!loading && events.length === 0 && (
        <div className={cn("text-center py-12", darkMode ? "text-gray-400" : "text-gray-500")}>
          <Clock className={cn("w-12 h-12 mx-auto mb-2", darkMode ? "text-gray-600" : "text-gray-300")} />
          <p>Nenhum evento de acesso encontrado</p>
        </div>
      )}
      {/* Event list — kept visible while re-loading with new filter */}
      {events.length > 0 && (
        <>
          {/* Subtle inline loading indicator when re-fetching */}
          {loading && (
            <div className="flex items-center justify-center py-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 opacity-60" />
            </div>
          )}
          <div className={cn("border rounded overflow-hidden", darkMode ? "border-gray-700" : "border-gray-200")}>
            {(() => {
              // Show AUTORIZADO POR column only when at least one event has that data
              const showAuthorizedBy = events.some(e => e.details?.authorized_by_name);
              const gridCols = showAuthorizedBy ? 'grid-cols-[90px_80px_1fr_160px]' : 'grid-cols-[90px_80px_1fr]';
              const headers  = showAuthorizedBy
                ? ['DATA / HORA', 'DIREÇÃO', 'DISPOSITIVO', 'AUTORIZADO POR']
                : ['DATA / HORA', 'DIREÇÃO', 'DISPOSITIVO'];

              return (
                <>
                  {/* Column headers */}
                  <div className={cn("grid px-3 py-2 border-b", gridCols, darkMode ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200")}>
                    {headers.map((col, i) => (
                      <span key={i} className={cn("text-[9px] tracking-widest font-semibold uppercase", darkMode ? "text-gray-500" : "text-gray-400")}>{col}</span>
                    ))}
                  </div>

                  {events.map((event, idx) => {
                    const { date, time } = formatTimestamp(event.event_timestamp);
                    const dir = event.direction?.toLowerCase();
                    const dirLabel = dir === 'entrada' ? 'ENTRADA' : dir === 'saida' ? 'SAÍDA' : dir === 'passagem' ? 'PASSAGEM' : '—';
                    const dirColor = dir === 'entrada'
                      ? darkMode ? 'text-emerald-400' : 'text-emerald-700'
                      : dir === 'saida'
                        ? darkMode ? 'text-amber-400' : 'text-amber-700'
                        : darkMode ? 'text-violet-400' : 'text-violet-700';

                    const isEven = idx % 2 === 0;
                    const rowBg  = isEven
                      ? darkMode ? 'bg-gray-900'    : 'bg-white'
                      : darkMode ? 'bg-gray-800/60' : 'bg-gray-50/50';

                    const authName = event.details?.authorized_by_name || null;
                    const authUnit = event.details?.unit || null;
                    const authResidentId = event.details?.authorized_by_resident_id || null;
                    const authTooltip = [authName, authUnit ? `(${authUnit})` : null].filter(Boolean).join(' ');

                    return (
                      <div
                        key={event.id}
                        className={cn("grid items-start px-3 py-2.5 border-b last:border-b-0", gridCols, rowBg, darkMode ? "border-gray-700/50" : "border-gray-100")}
                      >
                        {/* DATE / TIME */}
                        <div>
                          <p className={cn("text-xs font-mono font-medium", darkMode ? "text-gray-200" : "text-gray-800")}>{time}</p>
                          <p className={cn("text-[10px] font-mono", darkMode ? "text-gray-500" : "text-gray-400")}>{date}</p>
                        </div>

                        {/* DIRECTION */}
                        <span className={cn("text-[11px] font-semibold tracking-wide pt-0.5", dirLabel !== '—' ? dirColor : darkMode ? 'text-gray-600' : 'text-gray-400')}>
                          {dirLabel}
                        </span>

                        {/* DEVICE — primary focus, full name */}
                        <div className="min-w-0 pr-3">
                          <p className={cn("text-xs font-semibold", darkMode ? "text-gray-100" : "text-gray-900")}>
                            {event.device_name || <span className={cn(darkMode ? "text-gray-600" : "text-gray-400")}>—</span>}
                          </p>
                          {event.zone_name && (
                            <p className={cn("text-[10px]", darkMode ? "text-gray-500" : "text-gray-400")}>{event.zone_name}</p>
                          )}
                          {/* Event type badge — mirrors monitoramento labels. What each row IS
                              lives in utils/eventHelpers (accessHistoryBadge), so the badge, the
                              CSV and the PDF cannot drift apart. */}
                          {(() => {
                            const badge = accessHistoryBadge(event);
                            if (!badge) return null;

                            const { kind, label, tooltip } = badge;
                            return (
                              <span className={cn(
                                "inline-flex items-center gap-0.5 mt-1 text-[9px] font-medium px-1.5 py-px rounded",
                                kind === 'manual'
                                  ? darkMode ? "bg-amber-900/40 text-amber-400" : "bg-amber-50 text-amber-700"
                                  : kind === 'authenticated'
                                    ? darkMode ? "bg-blue-900/30 text-blue-400" : "bg-blue-50 text-blue-700"
                                    : kind === 'remote'
                                      ? darkMode ? "bg-violet-900/30 text-violet-400" : "bg-violet-50 text-violet-700"
                                      : darkMode ? "bg-teal-900/30 text-teal-400" : "bg-teal-50 text-teal-700"
                              )}>
                                {label}
                                <span className="relative group cursor-help leading-none">
                                  <Info className="w-2.5 h-2.5 flex-shrink-0" />
                                  <span className={cn(
                                    "absolute left-1/2 bottom-full mb-1.5 -translate-x-1/2 z-50",
                                    "w-52 rounded px-2 py-1.5 text-[10px] font-normal leading-snug pointer-events-none",
                                    "opacity-0 group-hover:opacity-100 transition-opacity duration-150",
                                    "shadow-lg",
                                    darkMode
                                      ? "bg-gray-700 text-gray-100 border border-gray-600"
                                      : "bg-gray-900 text-white"
                                  )}>
                                    {tooltip}
                                  </span>
                                </span>
                              </span>
                            );
                          })()}
                        </div>

                        {/* AUTORIZADO POR — only rendered when column is visible */}
                        {showAuthorizedBy && (
                          <div title={authTooltip || undefined}>
                            {authName ? (
                              authResidentId ? (
                                <button
                                  onClick={() => openPersonDetail(authResidentId, true, darkMode)}
                                  className={cn(
                                    "text-[10px] text-left leading-snug hover:underline transition-colors",
                                    darkMode ? "text-blue-400 hover:text-blue-300" : "text-blue-600 hover:text-blue-800"
                                  )}
                                >
                                  {authName}
                                </button>
                              ) : (
                                <p className={cn("text-[10px] leading-snug", darkMode ? "text-gray-300" : "text-gray-600")}>
                                  {authName}
                                </p>
                              )
                            ) : (
                              <span className={cn("text-[10px]", darkMode ? "text-gray-700" : "text-gray-300")}>—</span>
                            )}
                            {authUnit && (
                              <p className={cn("text-[9px] mt-0.5", darkMode ? "text-gray-500" : "text-gray-400")}>{authUnit}</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </>
              );
            })()}
          </div>
          {hasMore && (
            <div className="text-center">
              <button
                onClick={loadMore}
                disabled={loading}
                className={cn("px-6 py-2 text-blue-600 rounded-lg transition-colors disabled:opacity-50", darkMode ? "hover:bg-blue-900/30" : "hover:bg-blue-50")}
              >
                {loading ? 'Carregando...' : 'Carregar Mais'}
              </button>
            </div>
          )}
        </>
      )}

    </div>
  );
};
